import Fuse from 'fuse.js'
import { documents, columns, events } from '@/store/state'
import { isTestUno } from '@/config/testUno'
import { AGENDA_GENRE_EXCLUSIONS, type SearchQueryParams } from 'afpnews-api'
import type { Column } from '@/types'
import { normalize } from '@/lib/normalize'
import { normalizeEventId, pickEventName, type EventMatch } from '@/lib/events'

export const getDocumentById = (docId: string) => {
  const document = documents.value.find(d => d.id == docId)
  if (!document) throw new Error('Document not found')
  return document
}

export const documentExistsById = (docId: string) => {
  return documents.value.some(d => d.id == docId)
}

export const getEventById = (id: string) => {
  return events.value.find(e => e.id === id)
}

export interface SearchLocalEventsOptions {
  preferredLangs?: (string | undefined)[]
  limit?: number
  matchId?: boolean
}

// Recherche par nom (fuzzy) et, en option, par id (préfixe exact) parmi les
// événements connus localement (table Dexie `events`)
export const searchLocalEvents = (
  query: string,
  options: SearchLocalEventsOptions = {}
): EventMatch[] => {
  const { preferredLangs = [], limit = 5, matchId = false } = options
  const q = query.trim()
  if (q.length < 2) return []

  const items = events.value.flatMap(ev => {
    const name = pickEventName(ev, preferredLangs)
    return name ? [{ id: ev.id, name }] : []
  })

  const fuse = new Fuse(items, {
    keys: ['name'],
    threshold: 0.3,
    ignoreLocation: true,
    minMatchCharLength: 2
  })
  const nameMatches = fuse.search(q).map(r => r.item)

  if (!matchId) return nameMatches.slice(0, limit)

  const nq = normalize(q)
  const idMatches = items.filter(ev => normalize(ev.id).startsWith(nq))

  const seen = new Set<string>()
  const merged: EventMatch[] = []
  for (const ev of [...idMatches, ...nameMatches]) {
    if (seen.has(ev.id)) continue
    seen.add(ev.id)
    merged.push(ev)
    if (merged.length >= limit) break
  }
  return merged
}

export const topLocalEvents = (
  preferredLangs: (string | undefined)[] = [],
  limit = 5
): (EventMatch & { count: number })[] => {
  // Normalisé : des documents déjà persistés avant l'introduction de
  // normalizeEventId peuvent porter des ids en casse hétérogène.
  const counts = new Map<string, number>()
  for (const doc of documents.value) {
    for (const { id: rawId } of doc.events ?? []) {
      if (!rawId) continue
      const id = normalizeEventId(rawId)
      counts.set(id, (counts.get(id) ?? 0) + 1)
    }
  }

  return events.value
    .flatMap(ev => {
      const name = pickEventName(ev, preferredLangs)
      const count = counts.get(ev.id) ?? 0
      return name && count > 0 ? [{ id: ev.id, name, count }] : []
    })
    .sort((a, b) => b.count - a.count)
    .slice(0, limit)
}

export const getColumnById = (id: string) => {
  return columns.value.find(c => c.id === id)
}

export const getDocumentsIdsByColumnId = (columnId: string) => {
  return getColumnById(columnId)?.documentsIds ?? []
}

export const getDocumentsByColumnId = (columnId: string) => {
  // Filtre les docs manquants sans lever : le liveQuery peut émettre des ids pas encore (ré)hydratés.
  const docs = getDocumentsIdsByColumnId(columnId)
    .map((docId: string) => documents.value.find(d => d.id == docId))
    .filter((d): d is NonNullable<typeof d> => !!d)
  // `-test` n'existe qu'en UAT : filtrer sans condition est un no-op en prod.
  return docs.filter(d => !isTestUno(d.id))
}

export const getPreviousDocumentIdInColById = (
  columnId: string,
  docId: string
) => {
  const currentDocumentsIdsinColumn = getDocumentsIdsByColumnId(columnId)
  const currentDocIndexInColumn = currentDocumentsIdsinColumn.indexOf(docId)
  const previousDocumentId =
    currentDocumentsIdsinColumn[currentDocIndexInColumn + 1]
  return previousDocumentId
}

export const getNextDocumentIdInColById = (columnId: string, docId: string) => {
  const currentDocumentsIdsinColumn = getDocumentsIdsByColumnId(columnId)
  const currentDocIndexInColumn = currentDocumentsIdsinColumn.indexOf(docId)
  const nextDocument = currentDocumentsIdsinColumn[currentDocIndexInColumn - 1]
  return nextDocument
}

export const AGENDA_GENRE_IDS = AGENDA_GENRE_EXCLUSIONS.exclude
export const DEFAULT_EXCLUDED_GENRE_IDS = [
  'afpedtype:DocRobot',
  ...AGENDA_GENRE_IDS
]

export const excludeGenreIds = (columnId: string) => {
  const params = getColumnById(columnId)?.params
  return params?.genreid &&
    typeof params.genreid == 'object' &&
    'exclude' in params.genreid &&
    Array.isArray(params.genreid.exclude)
    ? params.genreid.exclude
    : []
}

export const excludeAgenda = (columnId: string) => {
  return AGENDA_GENRE_IDS.every(d => excludeGenreIds(columnId).includes(d))
}

// Bornes inclusives : un décalage de ±1s sauterait les documents publiés dans la même seconde que la borne.
export const getRefreshParamsByMode = (
  column: Column,
  mode: 'newer' | 'older' | 'reset',
  boundaryPublished?: Date | string
): SearchQueryParams => {
  const params: SearchQueryParams = structuredClone(column.params)
  switch (mode) {
    case 'reset':
      break
    case 'older':
      if (boundaryPublished) {
        params.dateTo = new Date(boundaryPublished).toISOString()
      }
      break
    case 'newer':
      if (boundaryPublished) {
        params.dateFrom = new Date(boundaryPublished).toISOString()
      }
      break
    default:
      throw new Error('Invalid mode')
  }
  return params
}
